import React, { useCallback, useContext, useRef, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import InfiniteScroll from 'react-infinite-scroller';
import { AppContext } from '../../App';
import {
  getBudgetLineLazy,
  getBudgetLineOrderStubsPage,
  getBudgetRelatedOrderStubsFromRetrieve,
} from '../../clients/budget_line_client';
import { formatDateStr, showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import BudgetCache from '../../config_and_helpers/budget_cache';
import OrderDetailModal from '../Order/OrderDetailModal';
import '../Glass/GlassStyle.css';

const LOG_CONTEXT = 'BudgetDetailModal';
/** Modal table scroller is ~40vh; list-page 1200px threshold never fires here. */
const ORDERS_MODAL_SCROLL_THRESHOLD_PX = 160;

/**
 * Format PI display from nested primary_investigator.
 *
 * @param {Object|null|undefined} budget
 * @returns {string}
 */
function formatPiLabel(budget) {
  const pi = budget?.primary_investigator;
  if (!pi) return '—';
  const name = `${pi.first_name || ''} ${pi.last_name || ''}`.trim();
  return name || pi.username || '—';
}

/**
 * Format related users from nested `related_users`.
 *
 * @param {Object|null|undefined} budget
 * @returns {string}
 */
export function formatBudgetRelatedUsersLabel(budget) {
  const users = Array.isArray(budget?.related_users) ? budget.related_users : [];
  if (!users.length) return '—';
  return (
    users
      .map(user => {
        const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        return name || user.username || (user.id != null ? `User ${user.id}` : '');
      })
      .filter(Boolean)
      .join(', ') || '—'
  );
}

/**
 * Budget `order_count` badge label (not Lab Data spend order_count).
 *
 * @param {*} count
 * @returns {string}
 */
export function formatBudgetOrderCountLabel(count) {
  const n = typeof count === 'number' && Number.isFinite(count) ? count : 0;
  return `${n} ${n === 1 ? 'order' : 'orders'}`;
}

/**
 * Append unique stubs by id.
 *
 * @param {Array<{ id: number, corporate_order_ref?: string }>} prev
 * @param {Array<{ id: number, corporate_order_ref?: string }>} incoming
 * @param {boolean} reset
 * @returns {Array<{ id: number, corporate_order_ref?: string }>}
 */
function mergeOrderStubs(prev, incoming, reset) {
  if (reset || !prev.length) return incoming;
  const seen = new Set(prev.map(stub => stub.id));
  return [...prev, ...incoming.filter(stub => !seen.has(stub.id))];
}

/**
 * Read-only Procure budget detail modal: identity + paginated related-order stubs.
 *
 * Hover prefetches retrieve and page 1 of `/orders/` (mapped to stubs).
 * Further pages load via InfiniteScroll in `.budget-detail-orders-scroll`
 * (160px threshold). Each `OrderDetailModal` fetches on hover of that
 * order id. Compact retrieve stubs skip `/orders/`.
 *
 * @param {Object} props
 * @param {Object} [props.budgetObj] - List-row fallback while retrieve loads
 * @param {string|number} [props.budgetId]
 */
const BudgetDetailModal = ({ budgetObj, budgetId }) => {
  const { token } = useContext(AppContext);
  const budgetIdToUse = budgetObj?.id ?? budgetId;
  const showRef = useRef(false);
  const stubsSourceRef = useRef(null);
  const orderStubsLoadedRef = useRef(false);
  const loadingOrderStubsRef = useRef(false);

  const [show, setShow] = useState(false);
  const [budget, setBudget] = useState(budgetObj || null);
  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);

  const [orderStubs, setOrderStubs] = useState([]);
  const [ordersNextPage, setOrdersNextPage] = useState(null);
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  const [loadingOrderStubs, setLoadingOrderStubs] = useState(false);
  const [orderStubsError, setOrderStubsError] = useState(null);
  const [orderStubsLoaded, setOrderStubsLoaded] = useState(false);
  const [ordersScrollEl, setOrdersScrollEl] = useState(null);

  const resetOrderStubsState = () => {
    stubsSourceRef.current = null;
    orderStubsLoadedRef.current = false;
    loadingOrderStubsRef.current = false;
    setOrderStubs([]);
    setOrdersNextPage(null);
    setOrdersHasMore(false);
    setOrderStubsLoaded(false);
    setOrderStubsError(null);
  };

  const applyCompleteRetrieveStubs = stubs => {
    stubsSourceRef.current = 'retrieve';
    orderStubsLoadedRef.current = true;
    setOrderStubs(stubs);
    setOrdersNextPage(null);
    setOrdersHasMore(false);
    setOrderStubsLoaded(true);
    setOrderStubsError(null);
  };

  const fetchOrdersPage = useCallback(
    async ({ nextPage = null, reset = false } = {}) => {
      if (!budgetIdToUse || !token) return;
      if (stubsSourceRef.current === 'retrieve') return;
      if (loadingOrderStubsRef.current) return;
      if (!reset && !nextPage && orderStubsLoadedRef.current) return;

      loadingOrderStubsRef.current = true;
      setLoadingOrderStubs(true);
      if (reset) {
        setOrderStubsError(null);
      }
      logInfo(LOG_CONTEXT, 'orders page start', {
        id: budgetIdToUse,
        paginating: Boolean(nextPage),
        prefetch: !showRef.current,
      });
      const result = await getBudgetLineOrderStubsPage(token, budgetIdToUse, {
        nextPage,
      });
      loadingOrderStubsRef.current = false;
      setLoadingOrderStubs(false);
      if (stubsSourceRef.current === 'retrieve') {
        logInfo(LOG_CONTEXT, 'orders page ignored; retrieve stubs present', {
          id: budgetIdToUse,
        });
        return;
      }
      if (!result.success) {
        const message =
          result.messages?.[0] || 'Failed to load related orders';
        logError(LOG_CONTEXT, 'orders page failed', {
          id: budgetIdToUse,
          status: result.status ?? null,
        });
        setOrderStubsError(message);
        setOrdersHasMore(false);
        if (
          showRef.current &&
          (result.status === 403 || result.status === 404)
        ) {
          showToast(message, 'error', 'top-right', 5000);
        }
        return;
      }
      const incoming = Array.isArray(result.data) ? result.data : [];
      stubsSourceRef.current = 'orders';
      orderStubsLoadedRef.current = true;
      setOrderStubs(prev => mergeOrderStubs(prev, incoming, reset || !nextPage));
      setOrdersNextPage(result.nextPage || null);
      setOrdersHasMore(!result.reachedEnd && Boolean(result.nextPage));
      setOrderStubsLoaded(true);
      setOrderStubsError(null);
      logInfo(LOG_CONTEXT, 'orders page success', {
        id: budgetIdToUse,
        count: incoming.length,
        reachedEnd: Boolean(result.reachedEnd),
      });
    },
    [budgetIdToUse, token]
  );

  const applyRelatedOrderStubs = useCallback(
    payload => {
      const parsed = getBudgetRelatedOrderStubsFromRetrieve(payload);
      if (parsed.present) {
        applyCompleteRetrieveStubs(parsed.stubs);
        logInfo(LOG_CONTEXT, 'order stubs from retrieve', {
          id: budgetIdToUse,
          source: parsed.source,
          count: parsed.stubs.length,
        });
        return;
      }
      if (orderStubsLoadedRef.current || stubsSourceRef.current === 'orders') {
        return;
      }
      const count =
        typeof payload?.order_count === 'number' ? payload.order_count : 0;
      if (count <= 0) {
        applyCompleteRetrieveStubs([]);
        return;
      }
      fetchOrdersPage({ reset: true });
    },
    [budgetIdToUse, fetchOrdersPage]
  );

  const fetchDetailedData = async ({ force = false } = {}) => {
    if (!budgetIdToUse || !token) return null;
    if (!force) {
      const cached = BudgetCache.get(budgetIdToUse);
      if (cached) {
        logInfo(LOG_CONTEXT, 'retrieve cache hit', {
          id: budgetIdToUse,
          piId:
            cached.primary_investigator?.id ??
            cached.primary_investigator_id ??
            null,
        });
        setDetailedData(cached);
        setBudget(cached);
        setLoadingDetails(false);
        applyRelatedOrderStubs(cached);
        return cached;
      }
      if (detailedData || loadingDetails) {
        if (detailedData) {
          applyRelatedOrderStubs(detailedData);
        }
        return detailedData;
      }
    }
    if (force) {
      resetOrderStubsState();
    }
    setLoadingDetails(true);
    setError(null);
    logInfo(LOG_CONTEXT, 'retrieve start', {
      id: budgetIdToUse,
      force,
    });
    const result = await getBudgetLineLazy(token, budgetIdToUse, { force });
    setLoadingDetails(false);
    if (!result.success) {
      const message =
        result.messages?.[0] || 'Failed to load budget details';
      logError(LOG_CONTEXT, 'retrieve failed', { id: budgetIdToUse });
      setError(message);
      showToast(message, 'error', 'top-right', 5000);
      return null;
    }
    setDetailedData(result.data);
    setBudget(result.data);
    logInfo(LOG_CONTEXT, 'retrieve success', {
      id: budgetIdToUse,
      cached: Boolean(result.cached),
      orderCount:
        typeof result.data?.order_count === 'number'
          ? result.data.order_count
          : null,
    });
    applyRelatedOrderStubs(result.data);
    return result.data;
  };

  const prefetchOnHover = () => {
    if (!budgetIdToUse || !token) return;
    logInfo(LOG_CONTEXT, 'hover prefetch', { id: budgetIdToUse });
    fetchDetailedData();
    const listCount =
      typeof budgetObj?.order_count === 'number' ? budgetObj.order_count : 0;
    if (listCount > 0 && stubsSourceRef.current !== 'retrieve') {
      fetchOrdersPage();
    }
  };

  const handleShow = () => {
    showRef.current = true;
    logInfo(LOG_CONTEXT, 'detail opened', { id: budgetIdToUse });
    setShow(true);
    fetchDetailedData();
    const listCount =
      typeof budgetObj?.order_count === 'number' ? budgetObj.order_count : 0;
    const retrieveCount =
      typeof detailedData?.order_count === 'number'
        ? detailedData.order_count
        : 0;
    if (
      stubsSourceRef.current !== 'retrieve' &&
      (listCount > 0 || retrieveCount > 0)
    ) {
      fetchOrdersPage();
    }
  };

  const handleClose = () => {
    showRef.current = false;
    setShow(false);
    setOrdersScrollEl(null);
  };

  const loadMoreOrders = () => {
    if (loadingOrderStubsRef.current) return;
    if (
      !ordersHasMore ||
      !ordersNextPage ||
      stubsSourceRef.current === 'retrieve'
    ) {
      return;
    }
    fetchOrdersPage({ nextPage: ordersNextPage });
  };

  if (!budgetIdToUse) {
    return null;
  }

  const display = detailedData || budget || budgetObj || {};
  const triggerLabel =
    budgetObj?.number ||
    budgetObj?.code ||
    display.number ||
    display.code ||
    `Budget #${budgetIdToUse}`;
  const orderCount =
    typeof detailedData?.order_count === 'number'
      ? detailedData.order_count
      : typeof budgetObj?.order_count === 'number'
        ? budgetObj.order_count
        : typeof display.order_count === 'number'
          ? display.order_count
          : 0;
  const showOrderRefColumn = orderStubs.some(
    stub => stub?.corporate_order_ref
  );

  return (
    <div>
      <Button
        variant="link"
        onClick={handleShow}
        onMouseEnter={prefetchOnHover}
        aria-label={`View details for ${triggerLabel}`}
        style={{
          padding: 0,
          border: 'none',
          background: 'none',
          color: '#0d6efd',
          textDecoration: 'underline',
        }}
      >
        {triggerLabel}
      </Button>

      {show && (
        <Modal
          show={show}
          onHide={handleClose}
          aria-labelledby="budget-detail-modal"
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title
              id="budget-detail-modal"
              className="d-flex align-items-center gap-2"
            >
              <span>
                Budget {display.number || display.code || budgetIdToUse}
                {display.name ? ` – ${display.name}` : ''}
              </span>
              <Badge bg="secondary" pill>
                {formatBudgetOrderCountLabel(orderCount)}
              </Badge>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div>
              {loadingDetails && (
                <div className="text-center mb-3">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading budget details...</p>
                </div>
              )}

              {error && (
                <div className="alert alert-danger mb-3">
                  <p className="mb-2">Error loading budget details: {error}</p>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => fetchDetailedData({ force: true })}
                  >
                    Retry
                  </Button>
                </div>
              )}

              {!error && (
                <Container fluid>
                  <Row className="mb-2">
                    <Col>
                      <p className="fs-6 fw-bold">Number:</p>
                    </Col>
                    <Col>
                      <p className="fs-6">
                        {display.number || display.code || '—'}
                      </p>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>
                      <p className="fs-6 fw-bold">Name:</p>
                    </Col>
                    <Col>
                      <p className="fs-6">{display.name || '—'}</p>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>
                      <p className="fs-6 fw-bold">Primary investigator:</p>
                    </Col>
                    <Col>
                      <p className="fs-6">{formatPiLabel(display)}</p>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>
                      <p className="fs-6 fw-bold">Related users:</p>
                    </Col>
                    <Col>
                      <p className="fs-6">
                        {formatBudgetRelatedUsersLabel(display)}
                      </p>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>
                      <p className="fs-6 fw-bold">Allocated:</p>
                    </Col>
                    <Col>
                      <p className="fs-6">
                        {display.allocated_amount ?? '—'}
                        {display.currency ? ` ${display.currency}` : ''}
                      </p>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>
                      <p className="fs-6 fw-bold">Active:</p>
                    </Col>
                    <Col>
                      <p className="fs-6">
                        {display.is_active === false ? 'No' : 'Yes'}
                      </p>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>
                      <p className="fs-6 fw-bold">Notes:</p>
                    </Col>
                    <Col>
                      <p className="fs-6">{display.notes || '—'}</p>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>
                      <p className="fs-6 fw-bold">Created at:</p>
                    </Col>
                    <Col>
                      <p className="fs-6">
                        {display.created_at
                          ? formatDateStr(display.created_at)
                          : '—'}
                      </p>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>
                      <p className="fs-6 fw-bold">Updated at:</p>
                    </Col>
                    <Col>
                      <p className="fs-6">
                        {display.updated_at
                          ? formatDateStr(display.updated_at)
                          : '—'}
                      </p>
                    </Col>
                  </Row>

                  <h5 className="mt-4 d-flex align-items-center gap-2">
                    Related orders
                    <Badge bg="secondary" pill>
                      {formatBudgetOrderCountLabel(orderCount)}
                    </Badge>
                  </h5>

                  {orderStubsError && (
                    <div className="alert alert-danger mb-3">
                      <p className="mb-2">
                        Error loading related orders: {orderStubsError}
                      </p>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          resetOrderStubsState();
                          fetchOrdersPage({ reset: true });
                        }}
                      >
                        Retry
                      </Button>
                    </div>
                  )}

                  {loadingOrderStubs &&
                    orderStubs.length === 0 &&
                    !orderStubsError && (
                      <div className="text-center py-2">
                        <Spinner animation="border" size="sm" role="status" />
                        <div className="mt-1">
                          <small className="text-muted">
                            Loading related orders...
                          </small>
                        </div>
                      </div>
                    )}

                  {orderStubsLoaded &&
                    !orderStubsError &&
                    orderStubs.length === 0 &&
                    !loadingOrderStubs && (
                      <p className="text-muted mb-0">
                        No orders linked to this budget.
                      </p>
                    )}

                  {orderStubs.length > 0 && (
                    <div
                      ref={node => {
                        if (node !== ordersScrollEl) {
                          setOrdersScrollEl(node);
                        }
                      }}
                      className="budget-detail-orders-scroll"
                    >
                      {ordersScrollEl && (
                        <InfiniteScroll
                          pageStart={0}
                          loadMore={loadMoreOrders}
                          hasMore={ordersHasMore}
                          initialLoad
                          useWindow={false}
                          getScrollParent={() => ordersScrollEl}
                          threshold={ORDERS_MODAL_SCROLL_THRESHOLD_PX}
                          loader={
                            loadingOrderStubs ? (
                              <div
                                key="budget-orders-loader"
                                className="text-center py-2"
                              >
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  role="status"
                                />
                                <div className="mt-1">
                                  <small className="text-muted">
                                    Loading more orders...
                                  </small>
                                </div>
                              </div>
                            ) : (
                              <div key="budget-orders-loader-placeholder" />
                            )
                          }
                        >
                          <Table
                            striped
                            bordered
                            hover
                            size="sm"
                            responsive
                            className="mt-2"
                          >
                            <thead>
                              <tr className="text-center">
                                <th>#</th>
                                <th>Order</th>
                                {showOrderRefColumn ? (
                                  <th>Order Ref</th>
                                ) : null}
                              </tr>
                            </thead>
                            <tbody>
                              {orderStubs.map((stub, index) => (
                                <tr
                                  key={stub.id}
                                  className="text-center align-middle italic-text"
                                >
                                  <td>{index + 1}</td>
                                  <td>
                                    <OrderDetailModal orderId={stub.id} />
                                  </td>
                                  {showOrderRefColumn ? (
                                    <td>
                                      {stub.corporate_order_ref || '—'}
                                    </td>
                                  ) : null}
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </InfiniteScroll>
                      )}
                    </div>
                  )}
                </Container>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default BudgetDetailModal;
